import { useRef } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
} from "framer-motion";
import { ArrowRight } from "lucide-react";
import { EASE } from "@/components/Reveal";

const LINES = [
  { text: "Digital Marketing", gold: false },
  { text: "That Helps Businesses", gold: false },
  { text: "Grow Smarter.", gold: true },
];

const PARTICLES = [
  { top: "8%", left: "12%", size: 5, dur: 7 },
  { top: "22%", right: "6%", size: 4, dur: 9 },
  { bottom: "18%", left: "4%", size: 3, dur: 8 },
  { bottom: "6%", right: "18%", size: 5, dur: 10 },
  { top: "55%", right: "0%", size: 3, dur: 6 },
];

function GoldOrbit() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 50, damping: 20 });
  const sy = useSpring(my, { stiffness: 50, damping: 20 });
  const rotateX = useTransform(sy, [-0.5, 0.5], [7, -7]);
  const rotateY = useTransform(sx, [-0.5, 0.5], [-7, 7]);
  const ringX = useTransform(sx, [-0.5, 0.5], [-14, 14]);
  const ringY = useTransform(sy, [-0.5, 0.5], [-14, 14]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => {
        mx.set(0);
        my.set(0);
      }}
      className="relative mx-auto aspect-square w-full max-w-[520px]"
      style={{ perspective: 1200 }}
      data-testid="hero-art"
    >
      <motion.div
        style={{ rotateX, rotateY }}
        className="absolute inset-0"
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.4, delay: 0.5, ease: EASE }}
      >
        <motion.div style={{ x: ringX, y: ringY }} className="absolute inset-0">
          <div className="absolute inset-0 animate-spin-slower rounded-full border border-gold/25" />
          <div className="absolute inset-[9%] animate-spin-reverse rounded-full border border-dashed border-gold/20" />
          <div className="absolute inset-[20%] animate-spin-slower rounded-full border border-gold/35" />
          <div className="absolute left-1/2 top-0 h-2 w-2 -translate-x-1/2 rounded-full bg-gold shadow-[0_0_16px_rgba(201,163,78,0.8)]" />
          <div className="absolute bottom-[9%] right-[14%] h-1.5 w-1.5 rounded-full bg-gold-light shadow-[0_0_12px_rgba(214,179,106,0.8)]" />
        </motion.div>

        <motion.div
          animate={{ y: [0, -15, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="absolute inset-[27%] overflow-hidden rounded-full border border-gold/40 shadow-[0_0_80px_rgba(201,163,78,0.18)]"
        >
          <img
            src="/assets/mk-logo.jpg"
            alt="MK Group Plus monogram"
            className="h-full w-full scale-110 object-cover"
          />
        </motion.div>

        {PARTICLES.map((p, i) => (
          <motion.span
            key={i}
            className="absolute rounded-full bg-gold/70"
            style={{
              top: p.top,
              left: p.left,
              right: p.right,
              bottom: p.bottom,
              width: p.size,
              height: p.size,
            }}
            animate={{ y: [0, -18, 0], opacity: [0.4, 1, 0.4] }}
            transition={{
              duration: p.dur,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.7,
            }}
          />
        ))}
      </motion.div>
    </div>
  );
}

export default function Hero({ onConsult }) {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden pt-20"
    >
      <div
        className="pointer-events-none absolute -right-40 top-1/4 h-[560px] w-[560px] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(201,163,78,0.08) 0%, transparent 65%)",
        }}
      />
      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-6 py-24 lg:grid-cols-2 lg:px-10">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
            className="mb-8 flex items-center gap-4"
          >
            <span className="chapter-line" />
            <span className="text-xs font-semibold uppercase tracking-[0.35em] text-gold">
              MK Group Plus LLC — UAE
            </span>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="font-serif text-5xl leading-[1.08] tracking-[-0.02em] text-cream lg:text-6xl"
          >
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.gold ? "italic text-gold" : ""}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{
                    duration: 1,
                    delay: 0.35 + i * 0.14,
                    ease: EASE,
                  }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subheadline"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
            className="mt-8 max-w-lg text-base leading-relaxed text-cream/60 md:text-lg"
          >
            We help brands increase visibility, generate qualified leads, and
            build a stronger online presence through strategic digital
            marketing.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-consultation-btn"
              onClick={() => onConsult()}
              className="btn-gold"
            >
              Book Free Consultation
              <ArrowRight className="h-4 w-4" />
            </button>
            <a
              data-testid="hero-explore-btn"
              href="/#services"
              className="btn-outline"
            >
              Explore Services
            </a>
          </motion.div>
        </div>

        <div className="hidden lg:block">
          <GoldOrbit />
        </div>
      </div>
    </section>
  );
}
